
import Vue, { PropType } from 'vue';
import moment from 'moment-timezone';
import { DbTimestamp, Runnit } from '@run-diffusion/shared';
import { db, Timestamp } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins';
import { BoardRow } from '@/utils/boardsApi';
import {
	BoardBindState,
	BoardLiveState,
	boardLiveState,
	stubRunnitFromRow,
} from './boardsListState';

/**
 * One board row, live. Postgres picked the row; this binds `runnits/{id}` and
 * hands its slot the board to render, plus where the binding has got to:
 *
 *   <runnit-board-live-doc :row="row" v-slot="{ runnit, state }">
 *     <runnit-board-row :runnit="runnit" :live-state="state" />
 *   </runnit-board-live-doc>
 *
 * Until the board arrives (and if its binding fails) the slot gets a stand-in
 * built from the Postgres row, so the row never waits on Firestore to show.
 * Renders nothing of its own: the slot's root is the root, which keeps a table
 * row a direct child of the table body.
 */
export default Vue.extend({
	name: 'RunnitBoardLiveDoc',
	props: {
		row: {
			type: Object as PropType<BoardRow>,
			required: true,
		},
	},
	data () {
		return {
			liveRunnit: null as Runnit | null,
			bindState: 'loading' as BoardBindState,
		};
	},
	computed: {
		state (): BoardLiveState {
			return boardLiveState(this.liveRunnit, this.bindState);
		},
		stubRunnit (): Runnit {
			return stubRunnitFromRow(
				this.row,
				(iso: string): DbTimestamp => Timestamp.fromMillis(moment(iso).valueOf()) as DbTimestamp,
			);
		},
		runnit (): Runnit {
			return this.state === 'live' ? this.liveRunnit : this.stubRunnit;
		},
	},
	watch: {
		'row.id': {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal !== oldVal) this.bindRunnit(newVal);
			},
		},
		state (newVal: BoardLiveState, oldVal: BoardLiveState) {
			// Once, on the way in: the list asks Postgres again after a mirror lag.
			if (newVal === 'removed' && oldVal !== 'removed') this.$emit('removed', this.row.id);
		},
	},
	methods: {
		async bindRunnit (runnitId: string) {
			this.bindState = 'loading';
			if (!runnitId) {
				if (this.$firestoreRefs && this.$firestoreRefs.liveRunnit) this.$unbind('liveRunnit');
				this.liveRunnit = null;
				return;
			}
			try {
				await this.$bind(
					'liveRunnit',
					db.doc(`runnits/${runnitId}`),
					get$bindFirestoreOptions(),
				);
				// A newer row took this binding over while this one resolved.
				if (this.row.id === runnitId) this.bindState = 'bound';
			} catch (error) {
				console.error('Error binding board:', error);
				if (this.row.id === runnitId) this.bindState = 'failed';
			}
		},
	},
	render () {
		const nodes = this.$scopedSlots.default
			? this.$scopedSlots.default({ runnit: this.runnit, state: this.state })
			: null;
		return Array.isArray(nodes) ? nodes[0] : nodes;
	},
});
